import { authedFetch, getToken } from '@/lib/api';
import { createContext, useContext, useEffect, useState } from 'react'

export type ThemePreference = 'dark' | 'light' | 'system'
type ResolvedTheme = 'dark' | 'light'

interface ThemeContextValue {
  preference: ThemePreference
  theme: ResolvedTheme
  setPreference: (p: ThemePreference) => void
}

const ThemeContext = createContext<ThemeContextValue>({
  preference: 'system',
  theme: 'dark',
  setPreference: () => {},
})

function getResolved(pref: ThemePreference): ResolvedTheme {
  if (pref === 'system') {
    try {
      return window.matchMedia?.('(prefers-color-scheme: light)')?.matches ? 'light' : 'dark'
    } catch {
      return 'dark'
    }
  }
  return pref
}

function applyTheme(resolved: ResolvedTheme) {
  document.documentElement.setAttribute('data-theme', resolved)
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Start from localStorage immediately (no flash)
  const [preference, setPreferenceState] = useState<ThemePreference>(() => {
    return (localStorage.getItem('tenon-theme-pref') as ThemePreference) || 'system'
  })
  const [theme, setTheme] = useState<ResolvedTheme>(() => {
    const pref = (localStorage.getItem('tenon-theme-pref') as ThemePreference) || 'system'
    return getResolved(pref)
  })

  // On mount: load from user profile (DB) and apply
  useEffect(() => {
    const token = getToken()
    if (!token) return
    authedFetch('/api/v1/profile', {}, { redirectOnAuthFailure: false })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.theme_preference) {
          const pref = data.theme_preference as ThemePreference
          setPreferenceState(pref)
          localStorage.setItem('tenon-theme-pref', pref)
          const resolved = getResolved(pref)
          setTheme(resolved)
          applyTheme(resolved)
        }
      })
      .catch(() => {}) // silently ignore — use localStorage fallback
  }, [])

  // Apply theme whenever preference changes
  useEffect(() => {
    const resolved = getResolved(preference)
    setTheme(resolved)
    applyTheme(resolved)
    localStorage.setItem('tenon-theme-pref', preference)
  }, [preference])

  // Track system preference changes
  useEffect(() => {
    if (preference !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: light)')
    const handler = (e: MediaQueryListEvent) => {
      const resolved = e.matches ? 'light' : 'dark'
      setTheme(resolved)
      applyTheme(resolved)
    }
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [preference])

  const setPreference = (p: ThemePreference) => {
    setPreferenceState(p)
    // Save to DB
    const token = getToken()
    if (token) {
      authedFetch('/api/v1/profile/theme', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ theme: p }),
      }, { redirectOnAuthFailure: false }).catch(() => {}) // fire and forget
    }
  }

  return (
    <ThemeContext.Provider value={{ preference, theme, setPreference }}>
      {children}
    </ThemeContext.Provider>
  )
}

export const useTheme = () => useContext(ThemeContext)
